import { useFormik } from 'formik'
import * as Yup from 'yup'

import { Select, Textarea } from '@loadsmart/loadsmart-ui'
import { Drawer, Field, Icon, LabeledValue, Layout, Text } from '@loadsmart/miranda-react'

import { type StopAppointment } from 'core/appointments/domain/Appointment'

import { getOpenDockInitialValuesFromAppointment } from '../../domain/Appointment'
import { type AppointmentCommonFormValues } from '../../infra/ShipmentRepository'
import { APPOINTMENT_MISSCHEDULING_REASON_OPTIONS } from '../../presenters/appointment'

export function getValidationSchema() {
  return Yup.object().shape({
    misschedulingReason: Yup.object().shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
    misschedulingNote: Yup.string()
      .nullable()
      .max(100, 'Note must be 100 characters or less')
      .when('misschedulingReason.value', {
        is: 'other',
        then: () => Yup.string().required('Must provide a note'),
      }),
  })
}

export function Scheduled({
  onReschedule,
  onCancelAppointment,
  appointment,
  rescheduleByCarrierForbidden = false,
}: {
  onReschedule: () => void
  onCancelAppointment: () => void
  appointment?: StopAppointment
  rescheduleByCarrierForbidden: boolean
}) {
  const initialValues = getOpenDockInitialValuesFromAppointment(appointment)
  const validationSchema = getValidationSchema()

  const { handleChange, values, isValid, isSubmitting, dirty } =
    useFormik<AppointmentCommonFormValues>({
      initialValues,
      validationSchema,
      onSubmit: () => undefined,
      enableReinitialize: true,
    })

  const disableSubmit = !isValid || isSubmitting || !dirty || rescheduleByCarrierForbidden

  return (
    <>
      <Drawer.Header>
        <Layout.Group align="center" gap="spacing-1">
          <Icon name="calendar" size="24px" />
          Scheduled
        </Layout.Group>
        <Drawer.Close />
      </Drawer.Header>
      <Drawer.Body>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <LabeledValue>
            <LabeledValue.Label>Warehouse</LabeledValue.Label>
            <LabeledValue.Value>TO DO</LabeledValue.Value>
          </LabeledValue>
          <LabeledValue>
            <LabeledValue.Label>Load type</LabeledValue.Label>
            <LabeledValue.Value>TO DO</LabeledValue.Value>
          </LabeledValue>
          <LabeledValue>
            <LabeledValue.Label>Docks</LabeledValue.Label>
            <LabeledValue.Value>TO DO</LabeledValue.Value>
          </LabeledValue>
          <LabeledValue>
            <LabeledValue.Label>Scheduled At</LabeledValue.Label>
            <LabeledValue.Value>TO DO</LabeledValue.Value>
          </LabeledValue>
          <Field label="Reschedule reason" size="small" required>
            <Select
              name="misschedulingReason"
              placeholder="Select reason"
              options={APPOINTMENT_MISSCHEDULING_REASON_OPTIONS}
              value={values.misschedulingReason}
              onChange={handleChange}
              hideClear
              disabled={rescheduleByCarrierForbidden}
            />
          </Field>
          <Field
            style={{ flex: 1 }}
            label="Note (max 100 characters)"
            required={values.misschedulingReason.value === 'other'}
          >
            <Textarea
              onInput={handleChange}
              name="misschedulingNote"
              value={values.misschedulingNote}
              aria-label="Note"
              maxLength={100}
              disabled={rescheduleByCarrierForbidden}
            />
          </Field>
          {rescheduleByCarrierForbidden && (
            <Layout.Box
              paddingY="spacing-4"
              paddingX="spacing-6"
              borderWidth="border-thin"
              borderRadius="border-radius-m"
              borderColor="color-border-input"
            >
              <Layout.Stack gap="spacing-1">
                <Text variant="body-md-bold">
                  This facility does not allow carriers to reschedule appointments.
                </Text>
                <Text variant="body-sm" color="color-text-tertiary">
                  Please contact the shipper or warehouse directly for any changes.
                </Text>
              </Layout.Stack>
            </Layout.Box>
          )}
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary style={{ marginRight: 'auto' }} onClick={onCancelAppointment}>
          Cancel appointment
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary disabled={disableSubmit} onClick={onReschedule}>
          Reschedule
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
